import type {
  EventVenue,
  EventCategoryMapping,
  EventCategory,
  EventFeaturedUser,
  UserSubscription,
  Event,
} from '@shady-pines/db';
import { gql } from 'graphql-tag';

export const GET_USER_PROFILES = gql`
  query GetUserProfiles($first: Int!, $after: Cursor) {
    profilesCollection(first: $first, after: $after) {
      edges {
        cursor
        node {
          __typename
          id
          name
          user_name
          device_type
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`;

export const GET_SHOWS = gql`
  query GetActiveShows($first: Int!, $after: Cursor) {
    shows_publicCollection(first: $first, after: $after, filter: { active: { eq: true } }) {
      edges {
        cursor
        node {
          __typename
          id
          name
          description
          day_of_week
          start_time
          end_time
          subscriber_count
          details_url
          default_push_title
          active
          dj_name_override
          default_push_message
          user_id
          heroku_user_id
          heroku_show_id
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`;

export const GET_ALL_SHOWS = gql`
  query GetAllShows($first: Int!, $after: Cursor) {
    showsCollection(
      first: $first
      after: $after
      filter: { deleted_at: { is: NULL } }
      orderBy: { start_time: AscNullsLast }
    ) {
      edges {
        cursor
        node {
          __typename
          id
          name
          active
          description
          day_of_week
          start_time
          end_time
          details_url
          default_push_title
          default_push_message
          user_id
          heroku_user_id
          heroku_show_id
          dj_name_override
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`;

export const GET_SHOWS_ON_DAY = gql`
  query GetShowsOnDay($first: Int!, $after: Cursor, $day_of_week: String!) {
    shows_publicCollection(
      first: $first
      after: $after
      filter: { active: { eq: true }, day_of_week: { eq: $day_of_week } }
      orderBy: { start_time: AscNullsFirst }
    ) {
      edges {
        cursor
        node {
          __typename
          id
          name
          description
          day_of_week
          start_time
          end_time
          subscriber_count
          details_url
          default_push_title
          default_push_message
          user_id
          heroku_user_id
          heroku_show_id
          dj_name_override
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`;

export const GET_MY_SHOWS = gql`
  query GetMyShows($first: Int!, $after: Cursor, $userId: UUID!) {
    shows_publicCollection(
      first: $first
      after: $after
      filter: { user_id: { eq: $userId }, active: { eq: true } }
    ) {
      edges {
        cursor
        node {
          __typename
          id
          name
          description
          day_of_week
          start_time
          end_time
          subscriber_count
          details_url
          default_push_title
          default_push_message
          current_push_title
          current_push_message
          user_id
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`;

export const GET_BUG_REPORTS = gql`
  query GetBugReports($first: Int!, $after: Cursor) {
    bug_reportsCollection(first: $first, after: $after) {
      edges {
        cursor
        node {
          __typename
          id
          title
          description
          platform
          device_type
          reporter_email
          severity
          status
          created_at
          user_id
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`;

export const ACTIVE_POPUPS_QUERY = gql`
  query ActivePopups($first: Int!, $after: Cursor, $currentTimestamp: Timestamp!) {
    popupsCollection(
      first: $first
      after: $after
      filter: {
        expires_at: { gt: $currentTimestamp }
        # enabled: { eq: true }
        active_at: { lt: $currentTimestamp }
      }
      orderBy: { expires_at: AscNullsFirst }
    ) {
      edges {
        cursor
        node {
          __typename
          id
          title
          description
          active_at
          enabled
          variant
          external_url
          dismissable
          video_url
          target_up_to_version
          target_after_version
          expires_at
          created_at
          updated_at
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`;

export const GET_SHOWS_BY_ID = gql`
  query GetShowsById($ids: [UUID!]) {
    shows_publicCollection(
      filter: { id: { in: $ids }, active: { eq: true }, deleted_at: { is: NULL } }
    ) {
      edges {
        cursor
        node {
          __typename
          id
          name
          description
          day_of_week
          start_time
          end_time
          subscriber_count
          details_url
          dj_name_override
          default_push_title
          default_push_message
          user_id
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`;

export type EventCollection = Event & {
  event_venues: EventVenue;
  event_category_mappings: {
    event_category_mappingsCollection: {
      edges: {
        node: EventCategoryMapping & {
          event_categories: EventCategory;
        };
      }[];
    };
  };
  event_featured_users: {
    event_featured_usersCollection: {
      edges: {
        node: EventFeaturedUser;
      }[];
    };
  };
  user_subscriptions: {
    user_subscriptionsCollection: {
      edges: {
        node: UserSubscription;
      }[];
    };
  };
};

export type PublicEvent = Event & {
  subscriber_count: number;
};

export const GET_EVENTS = gql`
  query GetEvents($first: Int!, $after: Cursor) {
    event_events_publicCollection(
      first: $first
      after: $after
      orderBy: { start_time: AscNullsFirst }
      filter: { deleted_at: { is: NULL } }
    ) {
      edges {
        cursor
        node {
          __typename
          id
          title
          description
          start_time
          end_time
          venue_id
          created_by
          active
          recurring
          featured
          status
          rejection_reason
          subscriber_count
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`;

export const GET_EVENT = gql`
  query GetEvent($id: UUID!) {
    event_events_publicCollection(filter: { id: { eq: $id }, deleted_at: { is: NULL } }) {
      edges {
        cursor
        node {
          __typename
          id
          title
          description
          start_time
          end_time
          venue_id
          created_by
          active
          recurring
          subscriber_count
          featured
          status
          rejection_reason
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`;

export const GET_VENUES = gql`
  query VenuesQuery {
    event_venuesCollection(filter: { deleted_at: { is: NULL } }) {
      edges {
        cursor
        node {
          __typename
          id
          name
          address
          city
          state
          postal_code
          url
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`;

export const GET_VENUE = gql`
  query GetVenue($id: UUID!) {
    event_venuesCollection(filter: { id: { eq: $id }, deleted_at: { is: NULL } }) {
      edges {
        cursor
        node {
          __typename
          id
          name
          address
          city
          state
          postal_code
          url
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`;

export const ALL_POPUPS_QUERY = gql`
  query AllPopups($first: Int!, $after: Cursor) {
    popupsCollection(
      first: $first
      after: $after
      filter: { deleted_at: { is: NULL } }
      orderBy: { expires_at: AscNullsFirst }
    ) {
      edges {
        cursor
        node {
          __typename
          id
          title
          description
          active_at
          enabled
          variant
          external_url
          expires_at
          created_at
          updated_at
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`;

export const GET_PROFILE_GQL = gql`
  query ProfilesQuery($ids: [UUID!]) {
    profilesCollection(filter: { id: { in: $ids }, deleted_at: { is: NULL } }) {
      edges {
        cursor
        node {
          __typename
          id
          name
          user_name
          role
          heroku_user_id
          device_type
          app_version
          shows_migrated
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`;

export const GET_EVENT_CATEGORY_MAPPINGS = gql`
  query GetEventCategoryMappings($ids: [UUID!]) {
    event_category_mappingsCollection(
      filter: { event_id: { in: $ids }, deleted_at: { is: NULL } }
    ) {
      edges {
        cursor
        node {
          id
          category_id
          event_categories {
            id
            name
          }
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`;

export const GET_EVENT_FEATURED_USERS = gql`
  query GetEventFeaturedUsers($ids: [UUID!]) {
    event_featured_usersCollection(filter: { event_id: { in: $ids }, deleted_at: { is: NULL } }) {
      edges {
        cursor
        node {
          id
          event_id
          user_id
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`;

export const TRACK_HISTORY_QUERY = gql`
  query TrackHistoryQuery($first: Int!, $after: Cursor) {
    track_history_recent_publicCollection(
      first: $first
      after: $after
      filter: {}
      orderBy: { created_at: DescNullsLast }
    ) {
      edges {
        cursor
        node {
          __typename
          id
          title
          artist
          album
          album_label
          artwork_url
          service_identifier
          acr_cloud_relevant
          is_show_title
          is_ad
          isrc_id
          apple_id
          spotify_id
          youtube_id
          recognition_confidence
          airing_show_id
          start_time
          created_at
          track_id
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`;

export const TRACK_HISTORY_FOR_TRACK_QUERY = gql`
  query TrackHistoryForTrackQuery($first: Int!, $after: Cursor, $trackId: UUID!) {
    track_history_recent_publicCollection(
      first: $first
      after: $after
      filter: { track_id: { eq: $trackId } }
      orderBy: { created_at: DescNullsLast }
    ) {
      edges {
        cursor
        node {
          __typename
          id
          title
          artist
          album
          album_label
          artwork_url
          service_identifier
          acr_cloud_relevant
          is_show_title
          is_ad
          isrc_id
          apple_id
          spotify_id
          youtube_id
          recognition_confidence
          airing_show_id
          start_time
          created_at
          track_id
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`;

export const GET_EVENT_CATEGORIES = gql`
  query GetCategories($first: Int!, $after: Cursor) {
    event_categoriesCollection(
      first: $first
      after: $after
      filter: { deleted_at: { is: NULL } }
      orderBy: { name: AscNullsFirst }
    ) {
      edges {
        cursor
        node {
          __typename
          id
          name
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`;

export const GET_TRACK = gql`
  query GetTrack($id: UUID!) {
    tracksCollection(filter: { id: { eq: $id } }) {
      edges {
        cursor
        node {
          __typename
          id
          title
          artist
          album
          album_label
          artwork_url
          isrc_id
          apple_id
          spotify_id
          youtube_id
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`;
